import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { Plus, Minus, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

// Projection fixe centrée sur la France métropolitaine. On la calcule
// une seule fois (viewBox 800×560, scale et center choisis pour un bon
// cadrage) et on l'exporte à Geographies / aux points → les 2 utilisent
// EXACTEMENT la même transformation donc les points tombent toujours
// au bon endroit sur la silhouette.
const MAP_WIDTH = 800;
const MAP_HEIGHT = 560;
const projection = geoMercator()
  .center([2.5, 46.5])
  .scale(2300)
  .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]);

/**
 * Carte live de la communauté — 100% SVG natif, aucune dépendance
 * réseau. Les données (points floutés) viennent de
 * `/api/price/public/users-map`. Les contours proviennent d'un
 * GeoJSON français simplifié servi en /france-metropole.geojson.
 *
 * Points :
 *   - Rouge plein pulsant : covoitureur en ligne
 *   - Rouge contour        : covoitureur hors ligne
 *   - Bleu                 : passager
 *
 * Les nouveaux arrivants (détectés entre deux polls) reçoivent une
 * onde choc visuelle pendant 2 s pour matérialiser l'inscription
 * live.
 */
export default function PriceLiveMap() {
  const [points, setPoints] = useState([]);
  const [counts, setCounts] = useState({
    members_total: 0,
    drivers_total: 0,
    drivers_online: 0,
    clients_total: 0,
  });
  const [bump, setBump] = useState(0);
  const prevKeysRef = useRef(new Set());
  const newPulseRef = useRef(new Map()); // key → spawnTime (ms)
  const [tick, setTick] = useState(0); // force re-render pour anim halo
  // Zoom & pan — contrôlé par ZoomableGroup. Center en lng/lat.
  const [view, setView] = useState({ coordinates: [2.5, 46.5], zoom: 1 });

  // ─── Fetch & poll ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/price/public/users-map`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const pts = Array.isArray(d?.points) ? d.points : [];
        // Détection des nouveaux points depuis le dernier fetch pour
        // déclencher l'onde choc "nouvelle inscription".
        const keys = new Set(pts.map((p) => _keyOf(p)));
        const now = performance.now();
        if (prevKeysRef.current.size > 0) {
          let newCount = 0;
          keys.forEach((k) => {
            if (!prevKeysRef.current.has(k)) {
              newPulseRef.current.set(k, now);
              newCount += 1;
            }
          });
          if (newCount > 0) setBump((b) => b + 1);
        }
        prevKeysRef.current = keys;
        setPoints(pts);
        setCounts(d.counts || {});
      } catch {
        // best-effort
      }
    };
    fetchData();
    const t = setInterval(fetchData, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // ─── Boucle d'animation (halos + onde choc) ─────────────────────
  useEffect(() => {
    let frame = 0;
    const render = () => {
      setTick((v) => (v + 1) % 1_000_000);
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, []);

  // ─── Projection des points en coords SVG ────────────────────────
  const projectedPoints = useMemo(() => {
    return points
      .map((p) => {
        const xy = projection([p.lng, p.lat]);
        if (!xy) return null;
        const [x, y] = xy;
        if (x < -40 || x > MAP_WIDTH + 40 || y < -40 || y > MAP_HEIGHT + 40) return null;
        return { ...p, x, y, key: _keyOf(p) };
      })
      .filter(Boolean);
  }, [points]);

  const now = performance.now();
  const PULSE_MS = 2200;
  const onlineHalo = (() => {
    // Oscillation du halo "en ligne" (0 → 1 → 0 toutes les 1.8s)
    const t = (now % 1800) / 1800;
    return { r: 6 + t * 14, opacity: (1 - t) * 0.5 };
  })();

  // Nettoyage des pulses expirés
  newPulseRef.current.forEach((spawnedAt, key) => {
    if (now - spawnedAt > PULSE_MS) newPulseRef.current.delete(key);
  });

  return (
    <section className="mx-auto max-w-6xl px-5 py-20" id="carte-live">
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-[#FFF3CC] street-border-thin text-[11px] font-[Outfit] font-black uppercase tracking-widest mb-3">
          🔴 En direct
        </div>
        <h2 className="font-[Outfit] text-3xl sm:text-4xl font-black leading-tight">
          La communauté grandit <span className="text-primary">en direct</span>
        </h2>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
        <StatPill label="Membres inscrits" value={counts.members_total || 0} tone="black" bump={bump} testid="live-map-count-members" />
        <StatPill label="Covoitureurs" value={counts.drivers_total || 0} tone="red" />
        <StatPill label="En ligne maintenant" value={counts.drivers_online || 0} tone="green" live />
      </div>

      {/* Carte 100% SVG */}
      <div
        className="relative rounded-3xl overflow-hidden street-border"
        style={{ boxShadow: "6px 6px 0 0 #1E0910", background: "linear-gradient(180deg, #FDF4E7 0%, #F5E6D3 100%)" }}
        data-testid="price-live-map"
      >
        <ComposableMap
          projection={projection}
          width={MAP_WIDTH}
          height={MAP_HEIGHT}
          viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <ZoomableGroup
            center={view.coordinates}
            zoom={view.zoom}
            minZoom={1}
            maxZoom={8}
            onMoveEnd={({ coordinates, zoom }) => setView({ coordinates, zoom })}
          >
            <Geographies geography="/france-metropole.geojson">
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey || geo.properties?.code || "fr"}
                    geography={geo}
                    fill="#F8D5A8"
                    stroke="#1E0910"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fill: "#F8D5A8" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Points — à l'intérieur du ZoomableGroup pour qu'ils
                suivent le zoom/pan. */}
            {projectedPoints.map((p) => {
              const isDriver = p.r === "d";
              const online = isDriver && p.o === 1;
              const spawnedAt = newPulseRef.current.get(p.key);
              const dot = online ? "#FF3B3B" : (isDriver ? "#FFA3A3" : "#3B82F6");
              const stroke = isDriver ? "#B00000" : "#1E3A8A";
              // La taille des points reste constante à l'écran : on
              // divise par le zoom courant pour compenser le scaling
              // du groupe. Idem pour les épaisseurs de traits et halos.
              const z = view.zoom || 1;

              let shockR = null;
              let shockA = 0;
              if (spawnedAt != null) {
                const dt = now - spawnedAt;
                const t = dt / PULSE_MS;
                if (t >= 0 && t <= 1) {
                  shockR = 3 + t * 22;
                  shockA = (1 - t) * 0.9;
                }
              }

              return (
                <g key={p.key} transform={`translate(${p.x} ${p.y})`}>
                  {online && (
                    <circle
                      r={onlineHalo.r / z}
                      fill="none"
                      stroke="#FF3B3B"
                      strokeWidth={1.5 / z}
                      opacity={onlineHalo.opacity}
                    />
                  )}
                  {shockR != null && (
                    <circle
                      r={shockR / z}
                      fill="none"
                      stroke={isDriver ? "#FF3B3B" : "#3B82F6"}
                      strokeWidth={1.6 / z}
                      opacity={shockA}
                    />
                  )}
                  <circle
                    r={(online ? 3.6 : 2.8) / z}
                    fill={dot}
                    stroke={stroke}
                    strokeWidth={0.6 / z}
                  />
                </g>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Boutons de contrôle zoom */}
        <div className="absolute top-3 right-3 flex flex-col gap-1" style={{ zIndex: 10 }}>
          <button
            type="button"
            onClick={() => setView((v) => ({ ...v, zoom: Math.min(v.zoom * 1.6, 8) }))}
            data-testid="live-map-zoom-in"
            aria-label="Zoom avant"
            className="h-9 w-9 rounded-xl bg-white street-border-thin grid place-items-center active:translate-y-[1px]"
            style={{ boxShadow: "2px 2px 0 0 #1E0910" }}
          >
            <Plus className="h-4 w-4" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => setView((v) => ({ ...v, zoom: Math.max(v.zoom / 1.6, 1) }))}
            data-testid="live-map-zoom-out"
            aria-label="Zoom arrière"
            className="h-9 w-9 rounded-xl bg-white street-border-thin grid place-items-center active:translate-y-[1px]"
            style={{ boxShadow: "2px 2px 0 0 #1E0910" }}
          >
            <Minus className="h-4 w-4" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => setView({ coordinates: [2.5, 46.5], zoom: 1 })}
            data-testid="live-map-zoom-reset"
            aria-label="Recentrer"
            className="h-9 w-9 rounded-xl bg-white street-border-thin grid place-items-center active:translate-y-[1px]"
            style={{ boxShadow: "2px 2px 0 0 #1E0910" }}
          >
            <RotateCcw className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        {/* Cartouche légende */}
        <div
          className="absolute left-3 bottom-3 rounded-xl bg-white street-border-thin px-3 py-2 text-[11px] font-[Outfit] font-black uppercase tracking-widest flex flex-col gap-1 sm:flex-row sm:gap-3"
          style={{ zIndex: 10, boxShadow: "3px 3px 0 0 #1E0910" }}
        >
          <LegendDot color="#FF3B3B">Covoitureur</LegendDot>
          <LegendDot color="#3B82F6">Passager</LegendDot>
        </div>
      </div>

      {/* Contact WhatsApp */}
      <div className="mt-6 flex flex-col items-center text-center">
        <p className="text-foreground/70 text-sm sm:text-base max-w-md mb-3">
          Une question avant de rejoindre la communauté ? Écris-nous sur WhatsApp, on répond vite.
        </p>
        <a
          href="https://wa.me/message/RU62OCIGWNZBE1"
          target="_blank"
          rel="noreferrer"
          data-testid="live-map-whatsapp-cta"
          className="inline-flex items-center gap-2.5 rounded-full px-5 py-3 sm:px-6 sm:py-3.5 bg-[#25D366] text-white font-[Outfit] font-black text-[14px] sm:text-[15px] uppercase tracking-widest street-border transition active:translate-y-[2px]"
          style={{ boxShadow: "4px 4px 0 0 #1E0910" }}
        >
          <WhatsAppIcon className="h-5 w-5" />
          Nous contacter sur WhatsApp
        </a>
      </div>
    </section>
  );
}

// Clé de dédup des points — même rôle + même position arrondie à 2 déc
function _keyOf(p) {
  return `${p.r}:${p.lat.toFixed(2)}:${p.lng.toFixed(2)}`;
}

function LegendDot({ color, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full border-2 border-black" style={{ background: color }} />
      <span>{children}</span>
    </span>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={className} fill="currentColor">
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.388-.42-.86-.42a3.39 3.39 0 0 0-.56.035c-.368.065-1.088.945-1.088 2.188 0 1.06.617 2.19 1.112 2.905 1.473 2.11 2.76 3.07 5.05 4.038.56.233 1.07.3 1.52.3.805 0 2.56-.503 2.56-1.56 0-.4-.03-.773-.372-1.03-.603-.355-1.388-.653-1.99-.898zM16.016 2C8.274 2 2 8.274 2 16.016c0 2.47.634 4.918 1.88 7.09L2 30l7.25-1.853a14.013 14.013 0 0 0 6.77 1.715c7.74 0 14.016-6.274 14.016-14.016C30.03 8.274 23.756 2 16.016 2zm0 25.517a11.552 11.552 0 0 1-5.906-1.618l-.42-.252-4.408 1.157 1.175-4.3-.273-.447a11.49 11.49 0 0 1-1.766-6.14c0-6.344 5.164-11.5 11.598-11.5 6.343 0 11.5 5.165 11.5 11.5 0 6.343-5.157 11.6-11.5 11.6z"/>
    </svg>
  );
}

function StatPill({ label, value, tone = "black", live = false, bump = 0, testid }) {
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => {
    if (bump) setAnimKey((k) => k + 1);
  }, [bump]);
  const bg = tone === "red" ? "#FF3B3B" : tone === "green" ? "#22c55e" : "#1E0910";
  return (
    <div
      className="rounded-2xl bg-white street-border-thin px-3 py-3 sm:px-4 sm:py-4 flex items-center justify-between gap-2"
      style={{ boxShadow: "3px 3px 0 0 #1E0910" }}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="text-[9px] sm:text-[10px] font-[Outfit] font-black uppercase tracking-widest text-foreground/50">{label}</div>
        <div key={animKey} className="font-[Outfit] text-2xl sm:text-3xl font-black leading-none mt-0.5 anim-bump">
          {value.toLocaleString("fr-FR")}
        </div>
      </div>
      {live ? (
        <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: bg, opacity: 0.6 }} />
          <span className="relative rounded-full h-2.5 w-2.5" style={{ background: bg }} />
        </span>
      ) : (
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: bg }} />
      )}
      <style>{`
        @keyframes anim-bump-kf {
          0% { transform: scale(1); }
          40% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        .anim-bump { animation: anim-bump-kf .6s cubic-bezier(.4,0,.2,1); }
      `}</style>
    </div>
  );
}
